import { useState, useEffect } from 'react';
import { Radio, Library, Activity, Layers, Users, FolderTree, Key } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Header } from './components/layout/Header';
import { VoiceStudio } from './components/studio/VoiceStudio';
import { VoiceManagement } from './components/voices/VoiceManagement';
import { AudioLibrary } from './components/library/AudioLibrary';
import { AnalyticsDashboard } from './components/analytics/AnalyticsDashboard';
import { SettingsView } from './components/settings/SettingsView';
import { TeamManagement } from './components/team/TeamManagement';
import { Voice, AudioGeneration, AuditLog } from './types';
import { PLATFORM_DEFAULTS } from '@ministry-ai/ui';
import { fetchApi } from './api';

type AppTab = 'studio' | 'library' | 'team' | 'voices' | 'analytics' | 'settings';

function MainApp() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<AppTab>('studio');

  // Shared State
  const [voices, setVoices] = useState<Voice[]>([]);
  const [generations, setGenerations] = useState<AudioGeneration[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);

  const role = currentUser?.role || 'USER';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin = role === 'ADMIN';

  // Safeguard tab access when switching roles
  useEffect(() => {
    if (!isSuperAdmin && (activeTab === 'voices' || activeTab === 'analytics' || activeTab === 'settings')) {
      setActiveTab('studio');
    }
    if (!isSuperAdmin && !isAdmin && activeTab === 'team') {
      setActiveTab('studio');
    }
  }, [role, activeTab, isSuperAdmin, isAdmin]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('📡 [App] Fetching initial voices from GET /api/v1/voices ...');
        const voicesRes = await fetchApi('/api/v1/voices');
        console.log('📡 [App] GET /api/v1/voices status:', voicesRes.status);
        if (voicesRes.ok) {
          const data = await voicesRes.json();
          console.log(`📡 [App] Loaded ${data.voices?.length || 0} voices from backend:`, data.voices);
          if (data.voices) setVoices(data.voices);
        }
        const gensRes = await fetchApi('/api/v1/generations');
        if (gensRes.ok) {
          const data = await gensRes.json();
          if (data.generations) setGenerations(data.generations);
        }
      } catch (err) {
        console.error('❌ [App] Failed to fetch initial data:', err);
      }
    };
    fetchData();
  }, []);

  const handleAddVoice = (newVoice: Voice) => {
    setVoices((prev) => [newVoice, ...prev]);

    const auditEntry: AuditLog = {
      id: `aud_${Date.now()}`,
      action: 'VOICE_CLONED',
      actorEmail: currentUser?.email || 'admin@doxatech.com',
      actorRole: currentUser?.role || 'SUPER_ADMIN',
      resourceId: newVoice.id,
      details: `Registered voice "${newVoice.name}" in category "${newVoice.categoryName || 'Standard'}"`,
      ipAddress: '192.168.1.1',
      timestamp: new Date().toISOString(),
    };
    setAuditLogs((prev) => [auditEntry, ...prev]);
  };

  const handleUpdateVoice = (updatedVoice: Voice) => {
    setVoices((prev) => prev.map((v) => (v.id === updatedVoice.id ? updatedVoice : v)));

    const auditEntry: AuditLog = {
      id: `aud_${Date.now()}`,
      action: 'VOICE_RENAMED',
      actorEmail: currentUser?.email || 'admin@doxatech.com',
      actorRole: currentUser?.role || 'SUPER_ADMIN',
      resourceId: updatedVoice.id,
      details: `Updated voice "${updatedVoice.name}" (Category: ${updatedVoice.categoryName}, Rate: ${updatedVoice.creditMultiplier}x)`,
      ipAddress: '192.168.1.1',
      timestamp: new Date().toISOString(),
    };
    setAuditLogs((prev) => [auditEntry, ...prev]);
  };

  const handleSyncVoices = async () => {
    console.log('🔄 [Voice Sync] Triggering POST /api/v1/voices/sync ...');
    try {
      const res = await fetchApi('/api/v1/voices/sync', { method: 'POST' });
      console.log('🔄 [Voice Sync] POST /api/v1/voices/sync HTTP status:', res.status, res.statusText);
      if (res.ok) {
        const data = await res.json();
        console.log('🔄 [Voice Sync] Full API response data:', data);
        console.log('🔄 [Voice Sync] Diagnostics from server:', data.diagnostics);
        console.log(`🔄 [Voice Sync] Received ${data.voices?.length || 0} total voices:`, data.voices);
        if (data.voices?.length) {
          setVoices(data.voices);
          return;
        }
      } else {
        const errText = await res.text();
        console.error('❌ [Voice Sync] Server returned non-OK status:', res.status, errText);
      }

      console.log('🔄 [Voice Sync] Fetching refreshed list from GET /api/v1/voices ...');
      const listRes = await fetchApi('/api/v1/voices');
      console.log('🔄 [Voice Sync] GET /api/v1/voices status:', listRes.status);
      if (listRes.ok) {
        const listData = await listRes.json();
        console.log(`🔄 [Voice Sync] GET /api/v1/voices returned ${listData.voices?.length || 0} voices:`, listData.voices);
        if (listData.voices?.length) {
          setVoices(listData.voices);
        }
      }
    } catch (err) {
      console.error('❌ [Voice Sync] Network/fetch error during sync:', err);
    }
  };

  const handleGenerationComplete = (newGen: AudioGeneration) => {
    setGenerations((prev) => [newGen, ...prev]);

    const auditEntry: AuditLog = {
      id: `aud_${Date.now()}`,
      action: 'GENERATION_COMPLETED',
      actorEmail: currentUser?.email || 'user@gracechurch.org',
      actorRole: currentUser?.role || 'USER',
      resourceId: newGen.id,
      details: `Generated ${newGen.durationSeconds}s audio for "${newGen.voiceName}" (Burned ${newGen.creditsBurned} credits)`,
      ipAddress: '192.168.1.42',
      timestamp: new Date().toISOString(),
    };
    setAuditLogs((prev) => [auditEntry, ...prev]);
  };

  const handleDeleteGeneration = (id: string) => {
    setGenerations((prev) => prev.filter((g) => g.id !== id));
  };

  const handleToggleFavorite = (voiceId: string) => {
    setVoices((prev) =>
      prev.map((v) => (v.id === voiceId ? { ...v, isFavorite: !v.isFavorite } : v))
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 selection:bg-indigo-500 selection:text-white transition-colors duration-200">
      {/* Top Header */}
      <Header />

      {/* Role-Specific Navigation Bar */}
      <div className="border-b border-slate-200 dark:border-slate-800/80 bg-white/80 dark:bg-slate-950/60 backdrop-blur-md sticky top-16 z-40 transition-colors">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-12 py-2.5 flex items-center justify-between">
          <nav className="flex items-center space-x-1.5 overflow-x-auto">
            {/* Common to all: Voice Studio & Audio Library */}
            <button
              onClick={() => setActiveTab('studio')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === 'studio'
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-900'
              }`}
            >
              <Radio className="w-4 h-4" />
              <span>Voice Studio</span>
            </button>

            <button
              onClick={() => setActiveTab('library')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === 'library'
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-900'
              }`}
            >
              <Library className="w-4 h-4" />
              <span>Audio Library ({generations.length})</span>
            </button>

            {/* Church Admin only: Church Team Management */}
            {isAdmin && (
              <button
                onClick={() => setActiveTab('team')}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                  activeTab === 'team'
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-900'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Church Team</span>
              </button>
            )}

            {/* Super Admin Only: Voice & Category Hub, Costs & Telemetry, System API Keys */}
            {isSuperAdmin && (
              <>
                <button
                  onClick={() => setActiveTab('voices')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === 'voices'
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-900'
                  }`}
                >
                  <FolderTree className="w-4 h-4" />
                  <span>Voice & Category Hub</span>
                </button>

                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === 'analytics'
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-900'
                  }`}
                >
                  <Activity className="w-4 h-4" />
                  <span>Costs & Telemetry</span>
                </button>

                <button
                  onClick={() => setActiveTab('settings')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === 'settings'
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-900'
                  }`}
                >
                  <Key className="w-4 h-4" />
                  <span>System API Keys</span>
                </button>
              </>
            )}
          </nav>

          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[11px] font-medium">
              {isSuperAdmin ? 'Platform Mode' : 'Church Tenant Connected'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Area: Fluid Full-Screen Layout */}
      <main className="flex-1 w-full px-4 sm:px-6 lg:px-10 xl:px-12 py-6 sm:py-8">
        {activeTab === 'studio' && (
          <VoiceStudio
            voices={voices.filter((v) => v.status === 'ACTIVE' || v.status === 'READY')}
            onGenerationComplete={handleGenerationComplete}
            onToggleFavorite={handleToggleFavorite}
          />
        )}

        {activeTab === 'library' && (
          <AudioLibrary generations={generations} onDeleteGeneration={handleDeleteGeneration} />
        )}

        {activeTab === 'team' && isAdmin && <TeamManagement />}

        {activeTab === 'voices' && isSuperAdmin && (
          <VoiceManagement
            voices={voices}
            onAddVoice={handleAddVoice}
            onUpdateVoice={handleUpdateVoice}
            onSyncVoices={handleSyncVoices}
            onNavigateToSettings={() => setActiveTab('settings')}
          />
        )}

        {activeTab === 'analytics' && isSuperAdmin && (
          <AnalyticsDashboard auditLogs={auditLogs} generations={generations} />
        )}

        {activeTab === 'settings' && isSuperAdmin && <SettingsView />}
      </main>

      {/* Clean Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800/80 bg-white dark:bg-slate-950 py-6 mt-auto transition-colors">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-12 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 dark:text-slate-400 gap-4">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
            <span className="font-semibold text-slate-700 dark:text-slate-300">{PLATFORM_DEFAULTS.title}</span>
            <span className="text-slate-400 dark:text-slate-700">•</span>
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">Theological Safety Active</span>
          </div>
          <div className="text-center sm:text-right text-[11px] text-slate-400 dark:text-slate-500">
            <span>{PLATFORM_DEFAULTS.attribution}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MainApp />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
